import { motion } from "framer-motion";
import { AlertTriangle, ChevronRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { StatusBadge } from "@/components/common/StatusBadge";
import { severityTone, humanize } from "@/lib/format";
import { cn } from "@/lib/utils";

export const AreasNeedingAttention = ({ items }) => {
    if (!items || items.length === 0) return null;
    return (
        <div>
            <div className="mb-4 flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-destructive/12 text-destructive">
                    <AlertTriangle className="h-[18px] w-[18px]" />
                </span>
                <h2 className="font-display text-xl font-semibold text-foreground">Areas Needing Attention</h2>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {items.map((a, i) => {
                    const tone = severityTone(a.severity);
                    const accent = {
                        destructive: "border-l-destructive",
                        warning: "border-l-warning",
                        accent: "border-l-accent",
                        muted: "border-l-muted-foreground",
                    }[tone];
                    return (
                        <motion.div
                            key={a.metric_code}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: i * 0.05 }}
                        >
                            <Card className={cn("h-full border-l-4 border-border/70 bg-card p-5 shadow-card", accent)}>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className="text-sm font-semibold text-foreground">{a.title}</p>
                                        <p className="mt-0.5 text-xs text-muted-foreground">{humanize(a.metric_code)}</p>
                                    </div>
                                    <StatusBadge tone={tone} label={humanize(a.severity)} dot={false} />
                                </div>
                                <div className="mt-3 flex items-baseline gap-2">
                                    <span className="font-display text-3xl font-semibold tabular text-foreground">
                                        {a.unit === "percent" ? `${a.value}%` : a.value}
                                    </span>
                                    <span className="text-sm text-muted-foreground">{a.message}</span>
                                </div>
                                {a.spiritual_context && (
                                    <p className="mt-3 flex items-start gap-1.5 rounded-lg bg-secondary/50 px-3 py-2 text-xs italic leading-relaxed text-muted-foreground">
                                        <ChevronRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                                        {a.spiritual_context}
                                    </p>
                                )}
                            </Card>
                        </motion.div>
                    );
                })}
            </div>
        </div>
    );
};
